import React, { useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  TextField,
  Chip,
  useTheme,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import { Autocomplete } from "@material-ui/lab";
import { CustomTheme } from "../../../../../../types/customTheme";

interface TagsSpotlightSectionProps {
  currentMenuData: any;
  availableTags?: any[];
  selectedTags: any[];
  onTagsChange: (tags: any[]) => void;
  spotlightText: string;
  onSpotlightTextChange: (text: string) => void;
  classes: any;
  wrapperData?: any;
}

const TagsSpotlightSection: React.FC<TagsSpotlightSectionProps> = ({
  currentMenuData,
  availableTags: availableTagsProp,
  selectedTags,
  onTagsChange,
  spotlightText,
  onSpotlightTextChange,
  classes,
  wrapperData,
}) => {
  const theme: CustomTheme = useTheme();

  console.log("selectedTags from prop:", selectedTags);
  console.log("availableTags from prop:", availableTagsProp);
  console.log("currentMenuData?.tags:", currentMenuData?.tags);

  // Get all available tags from props first, then from currentMenuData
  const availableTags = React.useMemo(() => {
    if (availableTagsProp && availableTagsProp.length > 0) {
      console.log("Using tags from props:", availableTagsProp);
      return availableTagsProp;
    }
    if (currentMenuData?.tags) {
      const tagsFromMenu = Object.values(currentMenuData.tags);
      console.log("Using tags from currentMenuData:", tagsFromMenu);
      return tagsFromMenu;
    }
    console.log("No tags available");
    return [];
  }, [availableTagsProp, currentMenuData]);

  // Helper function to get tag objects from tag IDs (handles both string and array)
  const getTagObjectsFromIds = (tagIds: any): any[] => {
    if (!tagIds) return [];
    
    let tagIdArray: string[] = [];
    
    // Check if tagIds is a string (comma-separated) or an array
    if (typeof tagIds === 'string') {
      // String format: "tag1,tag2,tag3"
      tagIdArray = tagIds.split(',').filter((id: string) => id.trim() !== '');
    } else if (Array.isArray(tagIds)) {
      // Array format: ["tag1", "tag2", "tag3"]
      tagIdArray = tagIds.filter((id: any) => id !== null && id !== undefined);
    } else if (typeof tagIds === 'object' && tagIds !== null) {
      // If it's an object, try to convert values to string array
      try {
        const values = Object.values(tagIds);
        if (values.length > 0) {
          // Convert each value to string and filter out empty values
          tagIdArray = values
            .filter((v: any) => v !== null && v !== undefined)
            .map((v: any) => String(v));
        }
      } catch (e) {
        console.error("Error parsing tagIds object:", e);
        return [];
      }
    }
    
    if (!tagIdArray.length || !availableTags.length) return [];
    
    return tagIdArray
      .map((id) => {
        // Find by id (string or number)
        const tag = availableTags.find(
          (tag: any) => tag.id === id || tag.id?.toString() === id?.toString()
        );
        return tag;
      })
      .filter((tag) => tag !== undefined);
  };

  // Initialize selectedTags from wrapperData.tags when component mounts or wrapperData changes
  useEffect(() => {
    console.log("wrapperData?.tags:", wrapperData?.tags);
    console.log("availableTags:", availableTags);

    if (
      wrapperData?.tags &&
      wrapperData.tags !== null &&
      wrapperData.tags !== undefined &&
      availableTags.length > 0
    ) {
      // wrapperData.tags can be string ("tag1,tag2,tag3") or array (["tag1", "tag2", "tag3"])
      const initialTags = getTagObjectsFromIds(wrapperData.tags);
      console.log("Initial tags from wrapperData:", initialTags);
      if (
        initialTags.length > 0 &&
        JSON.stringify(initialTags) !== JSON.stringify(selectedTags)
      ) {
        onTagsChange(initialTags);
      }
    } else if (
      (wrapperData?.tags === null || wrapperData?.tags === undefined || wrapperData?.tags === '') &&
      selectedTags.length > 0
    ) {
      console.log("Clearing tags - wrapper has no tags");
      onTagsChange([]);
    }
  }, [wrapperData?.tags, availableTags]);

  const handleTagsChange = (event: any, newValue: any[]) => {
    console.log("Tags changed to:", newValue);
    onTagsChange(newValue);
  };

  const handleDeleteTag = (index: number) => {
    const newTags = selectedTags.filter((_, i) => i !== index);
    console.log("Tag deleted, new tags:", newTags);
    onTagsChange(newTags);
  };

  return (
    <Paper
      elevation={0}
      style={{
        marginBottom: 24,
        borderRadius: 16,
        backgroundColor: theme.palette.background.menuProductColor,
        padding: 24,
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Box display="flex" alignItems="center" mb={4}>
        <Box
          style={{
            backgroundColor: "#5e35b1",
            borderRadius: 8,
            padding: 6,
            marginRight: 12,
          }}
        >
          <Icon icon="lucide:tag" width="20" style={{ color: "white" }} />
        </Box>
        <Box>
          <Typography variant="h6" style={{ fontWeight: "bold" }} align="left">
            Tags & Spotlight
          </Typography>
          <Typography variant="caption" align="left">
            Organise and highlight this wrapper.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Tags Column */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="body2"
            style={{ marginBottom: 8, fontWeight: 500 }}
            align="left"
          >
            Tags
          </Typography>

          {/* Autocomplete Input */}
          <Autocomplete
            multiple
            size="small"
            options={availableTags}
            getOptionLabel={(option: any) => option?.name || ""}
            value={selectedTags}
            onChange={handleTagsChange}
            classes={{ root: classes.autoComplete }}
            renderTags={() => null}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  availableTags.length > 0 ? "Select tags" : "No tags available"
                }
                className={classes.textField}
              />
            )}
          />

          {/* ✅ Selected Tags with gap between them */}
          <Box 
            display="flex" 
            flexWrap="wrap" 
            style={{ 
              marginTop: "8px",
              gap: "6px",
            }}
          >
            {selectedTags.map((option: any, index: number) => (
              <Chip
                key={option?.id || index}
                label={option?.name || ""}
                className={classes.tagChip}
                onDelete={() => handleDeleteTag(index)}
                deleteIcon={
                  <Icon 
                    icon="lucide:x" 
                    width="12" 
                    height="12" 
                    style={{ 
                      fontSize: "12px",
                      marginRight: "2px",
                      color: "white"
                    }} 
                  />
                }
                size="small"
              />
            ))}
          </Box>

          {/* Show message if no tags available */}
          {availableTags.length === 0 && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginTop: 8, display: "block" }}
            >
              No tags available. Please add tags in the menu settings.
            </Typography>
          )}
        </Grid>

        {/* Spotlight Column */}
        <Grid item xs={12} md={6}>
          <Typography
            variant="body2"
            style={{ marginBottom: 8, fontWeight: 500 }}
            align="left"
          >
            Spotlight
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Best seller!"
            className={classes.textField}
            value={spotlightText}
            onChange={(e) => onSpotlightTextChange(e.target.value)}
            InputProps={{
              style: {},
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TagsSpotlightSection;